import {
  assertSkillContentEditable,
  skillBundleWithLocalName,
} from "./skillGitUpdates";
import type { Transaction } from "@rocicorp/zero";
import { hashBlob } from "./commitFiles";
import { zql } from "./generatedDrizzleSchema";
import {
  composeSkillMd,
  parseStoredSkillFile,
  parseSkillMd,
  serializeStoredSkillFile,
  SKILL_MD_PATH,
  type StoredSkillFile,
  validateSkillBundle,
  validateSkillPath,
  validateStoredSkillFile,
} from "./skills/spec";

export { SKILL_MD_PATH } from "./skills/spec";

type SkillContent = {
  name: string;
  description: string;
  instructions: string;
};

export async function readSkillFiles(
  tx: Transaction,
  skillId: string
): Promise<Record<string, StoredSkillFile>> {
  const rows = await tx.run(
    zql.skillFiles.where("skillId", skillId).related("blob")
  );
  return Object.fromEntries(
    rows.flatMap((row) =>
      row.blob
        ? [[row.path, parseStoredSkillFile(row.blob.content)] as const]
        : []
    )
  );
}

async function writeSkillFile(
  tx: Transaction,
  skillId: string,
  tenantId: string,
  path: string,
  file: StoredSkillFile,
  options: { validateManifest?: boolean } = {}
) {
  await writeSkillFiles(tx, skillId, tenantId, { [path]: file }, options);
}

async function writeSkillFiles(
  tx: Transaction,
  skillId: string,
  tenantId: string,
  files: Record<string, StoredSkillFile>,
  options: { validateManifest?: boolean } = {}
) {
  const hashed = await Promise.all(
    Object.entries(files).map(async ([path, file]) => {
      validateStoredSkillFile(path, file);
      return {
        path,
        file,
        ...(await hashBlob(serializeStoredSkillFile(file))),
      };
    })
  );
  const hashes = [...new Set(hashed.map((file) => file.hash))];
  const [existingFiles, existingBlobs] = await Promise.all([
    tx.run(zql.skillFiles.where("skillId", skillId)),
    hashes.length === 0
      ? []
      : tx.run(
          zql.blobs.where("hash", "IN", hashes).where("tenantId", tenantId)
        ),
  ]);
  const existingByPath = new Map(
    existingFiles.map((file) => [file.path, file.blobHash])
  );
  const existingHashes = new Set(existingBlobs.map((blob) => blob.hash));

  for (const file of hashed) {
    if (!existingHashes.has(file.hash)) {
      await tx.mutate.blobs.upsert({
        hash: file.hash,
        content: file.content,
        tenantId,
      });
      existingHashes.add(file.hash);
    }
    if (existingByPath.get(file.path) !== file.hash) {
      await tx.mutate.skillFiles.upsert({
        skillId,
        path: file.path,
        blobHash: file.hash,
        tenantId,
      });
    }
  }

  const skillMd = files[SKILL_MD_PATH];
  if (skillMd && options.validateManifest !== false) {
    if (skillMd.encoding !== "utf8")
      throw new Error("SKILL.md must be UTF-8 text");
    const parsed = parseSkillMd(skillMd.content);
    await tx.mutate.skills.update({
      id: skillId,
      name: parsed.manifest.name,
      description: parsed.manifest.description,
      instructions: parsed.instructions,
    });
  }
}

export async function upsertSkillFiles(
  tx: Transaction,
  skillId: string,
  tenantId: string,
  files: Record<string, StoredSkillFile>
) {
  await assertSkillContentEditable(tx, skillId);
  validateSkillBundle({
    ...(await readSkillFiles(tx, skillId)),
    ...files,
  });
  await writeSkillFiles(tx, skillId, tenantId, files);
}

export async function deleteSkillFile(
  tx: Transaction,
  skillId: string,
  path: string
) {
  await assertSkillContentEditable(tx, skillId);
  validateSkillPath(path);
  if (path === SKILL_MD_PATH) throw new Error("SKILL.md cannot be deleted");
  const [existing] = await tx.run(
    zql.skillFiles.where("skillId", skillId).where("path", path)
  );
  if (existing) await tx.mutate.skillFiles.delete({ skillId, path });
}

export async function replaceSkillFiles(
  tx: Transaction,
  skillId: string,
  tenantId: string,
  files: Record<string, StoredSkillFile>
) {
  const parsed = validateSkillBundle(files);
  const [skill] = await tx.run(zql.skills.where("id", skillId));
  if (!skill) throw new Error("Skill not found");
  const syncedFiles = skillBundleWithLocalName(files, skill.name);
  const existing = await tx.run(zql.skillFiles.where("skillId", skillId));
  const incoming = new Set(Object.keys(syncedFiles));

  await writeSkillFiles(tx, skillId, tenantId, syncedFiles, {
    validateManifest: false,
  });
  for (const row of existing) {
    if (!incoming.has(row.path)) {
      await tx.mutate.skillFiles.delete({ skillId, path: row.path });
    }
  }
  await tx.mutate.skills.update({
    id: skillId,
    name: skill.name,
    description: parsed.manifest.description,
    instructions: parsed.instructions,
    fresh: false,
  });
}

export async function writeSkillMd(
  tx: Transaction,
  skillId: string,
  tenantId: string,
  content: SkillContent
) {
  const existing = (await readSkillFiles(tx, skillId))[SKILL_MD_PATH];
  let manifest = {
    name: content.name,
    description: content.description,
  };
  if (existing?.encoding === "utf8") {
    try {
      manifest = {
        ...parseSkillMd(existing.content).manifest,
        name: content.name,
        description: content.description,
      };
    } catch {
      // Legacy updates repair malformed SKILL.md content from the relational projection.
    }
  }
  const hasOptionalManifestFields =
    "license" in manifest ||
    "compatibility" in manifest ||
    "metadata" in manifest ||
    "allowedTools" in manifest;
  const skillMd = hasOptionalManifestFields
    ? composeSkillMd({ manifest, instructions: content.instructions })
    : `---
name: ${JSON.stringify(content.name)}
description: ${JSON.stringify(content.description)}
---

${content.instructions}`;
  await writeSkillFile(
    tx,
    skillId,
    tenantId,
    SKILL_MD_PATH,
    {
      encoding: "utf8",
      content: skillMd,
    },
    { validateManifest: false }
  );
}
